import React, { useEffect, useState, useRef, memo } from 'react';
import { Icon } from '@iconify/react';
import {
  isRenderableIcon,
  isValidIconEntryForEditor,
  normalizeIconObject,
} from '../../../utils/iconEntries';
import {
  getFlipTransform,
  getLegendPosition,
} from '../../../utils/legendGeometry';
import { getContrastingColor } from '../../../utils/colorHelpers';

const InlineSvgIcon = ({
  svgInnerMarkup,
  svgAttributes = {},
  viewBox,
  width,
  height,
  preserveAspectRatio = 'none',
  fill,
  stroke,
  strokeWidth,
}) => {
  if (!svgInnerMarkup) {
    return null;
  }

  return (
    <svg
      {...svgAttributes}
      viewBox={viewBox || svgAttributes.viewBox}
      width={width}
      height={height}
      preserveAspectRatio={preserveAspectRatio}
    >
      <g
        style={{
          color: fill,
          stroke,
          strokeWidth,
        }}
        dangerouslySetInnerHTML={{ __html: svgInnerMarkup }}
      />
    </svg>
  );
}; // renders sanitized uploaded SVG markup inline without nesting the original <svg>

const MissingUserIconAssetPlaceholder = ({ width, height, color }) => {
  const numericWidth = Number.parseFloat(String(width)) || 0;
  const numericHeight = Number.parseFloat(String(height)) || 0;
  const fontSize = Math.max(3, Math.min(numericWidth, numericHeight) * 0.55);

  return (
    <g className="dont-include-in-download">
      <rect
        width={width}
        height={height}
        rx="1"
        ry="1"
        fill="none"
        stroke={color}
        strokeDasharray="2 1.5"
        strokeWidth="0.5"
        opacity="0.5"
      />

      <text
        x={numericWidth / 2}
        y={numericHeight / 2}
        fill={color}
        fontSize={fontSize}
        textAnchor="middle"
        dominantBaseline="central"
        opacity="0.75"
        pointerEvents="none"
      >
        ?
      </text>
    </g>
  );
}; // renders a non-exported placeholder when an uploaded icon asset cannot be loaded

const RenderKeycapIcons = memo(
  // using React.memo to ensure that RenderKeycapIcons only re-renders when its props change (before that, it re-rendered every time a setHoveredKeycap was triggered in the KeycapOverlay.js file)
  ({
    keycapColor,
    selectedKeycapRowIndex,
    selectedKeycapIndex,
    selectedIconIndex,
    icons,
    userIconAssetsById = {},
    refreshUserIconAsset,
    innerDPath,
    innerDPath1mmPadding,
    rowIndex,
    keycapIndex,
    printableAreas,
    showGrid,
    showBoundingBox,
    showPrintableArea,
    selectedEditor,
    dataComingFromLegendsEditor,
    dataComingFromProcessOrderScreen,
    orderItemIndex,
    runIndex,
  }) => {
    const {
      innerRect1X,
      innerRect1Y,
      innerRect1Width,
      innerRect1Height,
      innerRect2X,
      innerRect2Y,
      innerRect2Width,
      innerRect2Height,
    } = printableAreas; // the keycap type's printable rectangles (keycapPrintableAreas.js): the clip path and the printable-area outline below, and getLegendPosition takes the object

    const iconRefs = useRef({}); // contains the refs for all Icon components being rendered in this component
    const [dimensionsMap, setDimensionsMap] = useState({}); // contains the bbox dimensions of all icons being rendered in this component
    const pathRef = useRef(null); // a reference to the inner path of the keycap (innerDPath1mmPadding)
    const [bbox, setBBox] = useState(null); // bounding box of the inner path of the keycap (innerDPath1mmPadding)

    const clipPathId = dataComingFromProcessOrderScreen
      ? `icon-clipPath-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `icon-clipPath-${rowIndex}-${keycapIndex}`; // unique id
    const innerPathId = dataComingFromProcessOrderScreen
      ? `icon-innerPath-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `icon-innerPath-${rowIndex}-${keycapIndex}`; // unique id

    /* Printable Area is currently disabled — uncomment together with the printable area path below:
    const printableAreaId = dataComingFromProcessOrderScreen
      ? `icon-printable-area-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `icon-printable-area-${rowIndex}-${keycapIndex}`; // unique id
    */

    useEffect(() => {
      if (pathRef.current) {
        const boundingBox = pathRef.current.getBBox();
        setBBox(boundingBox);
      }
    }, [innerDPath1mmPadding]); // calculating the bounding box of the innerDPath1mmPadding path (after it renders)

    useEffect(() => {
      let rafId; // We declare a variable rafId to store the identifier returned by requestAnimationFrame. This allows us to cancel the scheduled animation frame if necessary.

      if (icons) {
        // only try calculations on icons, if icons exist:
        const measureIcons = () => {
          const newDimensions = {};
          let allMeasured = true; // Flag to check if all icons have been measured

          Object.entries(icons)
            .filter(([_, iconObj]) => isRenderableIcon(iconObj))
            .forEach(([index, iconObj]) => {
              // iterate over all visible icons:
              const normalizedIcon = normalizeIconObject(iconObj);
              const source = normalizedIcon.source;
              const { style, isActiveStates, assetId, format } = normalizedIcon;

              if (source === 'user_asset' && format === 'png') {
                // PNGs are rendered as image elements, so there is no bounding box to measure
                newDimensions[index] = {
                  width: Number.parseFloat(String(style?.width)) || 0,
                  height: Number.parseFloat(String(style?.height)) || 0,
                  offsetX: 0,
                  offsetY: 0,
                }; // use the stored style dimensions as the bounding box
                return; // skip calculation of the bounding box
              }

              if (source === 'user_asset' && format === 'svg') {
                const asset = getUserIconAsset(assetId); // get the user uploaded SVG markup

                if (!asset?.svgInnerMarkup) {
                  // if the SVG markup has not been resolved yet:
                  allMeasured = false; // wait with the calculation of the SVGs bounding box
                  return; // don't try to calculate the bounding box of the current SVG in this run yet
                }
              }

              const svgElement = iconRefs.current[index]?.querySelector('svg'); // Attempt to access the SVG element within the rendered icon

              if (svgElement) {
                // If the SVG element is available, perform measurement
                const bbox = svgElement.getBBox(); // Get the bounding box of the SVG content

                const strokedElements =
                  svgElement.querySelectorAll('*[stroke]'); // Find all child elements of the svg with a stroke attribute
                let maxStrokeWidth = isActiveStates?.stroke
                  ? parseFloat(style?.strokeWidth)
                  : 0; // Use the strokeWidth from the icon's style object or 0 if not defined or active

                strokedElements.forEach((el) => {
                  // Iterate through all stroked elements:
                  const computedStyle = window.getComputedStyle(el); // Retrieve all computed styles for the current SVG element
                  const strokeWidth =
                    parseFloat(computedStyle.strokeWidth) || 0; // get the strokeWidth of the current SVG element
                  if (strokeWidth > maxStrokeWidth) {
                    maxStrokeWidth = strokeWidth; // If the current stroke width is greater, update maxStrokeWidth with this new value
                  }
                });

                const viewBox = svgElement.viewBox.baseVal;
                const renderedWidth = svgElement.width.baseVal.value;
                const renderedHeight = svgElement.height.baseVal.value;

                if (
                  !viewBox.width ||
                  !viewBox.height ||
                  !renderedWidth ||
                  !renderedHeight
                ) {
                  newDimensions[index] = {
                    width: Number.parseFloat(String(style?.width)) || 0,
                    height: Number.parseFloat(String(style?.height)) || 0,
                    offsetX: 0,
                    offsetY: 0,
                  };
                  return;
                }

                // calculate the scales to adjust SVG dimensions from its internal coordinate system defined by viewBox to the actual rendered dimensions on the screen:
                const scaleX = viewBox.width / renderedWidth;
                const scaleY = viewBox.height / renderedHeight;

                // Calculate the unscaled dimensions of the bounding box (these are the dimension values of the svg, before any scaling is applied during rendering):
                const unscaledWidth = (bbox.width + maxStrokeWidth) / scaleX;
                const unscaledHeight = (bbox.height + maxStrokeWidth) / scaleY;

                // Calculate the offset from the SVGs (0,0) to the bounding box start position:
                const offsetX = (bbox.x - maxStrokeWidth / 2) / scaleX;
                const offsetY = (bbox.y - maxStrokeWidth / 2) / scaleY;

                // Store the dimensions and offsets in the newDimensions object:
                newDimensions[index] = {
                  width: unscaledWidth,
                  height: unscaledHeight,
                  offsetX,
                  offsetY,
                };
              } else {
                allMeasured = false; // If the SVG element is not yet available, set allMeasured to false
              }
            });

          if (allMeasured) {
            setDimensionsMap(newDimensions); // If all SVG elements have been measured, update the dimensionsMap state
          } else {
            rafId = requestAnimationFrame(measureIcons); // If not all SVG elements are available (i.e., svgElement is still null for some icons), the measureIcons function schedules itself to run again on the next animation frame. This loop continues until all SVG elements are available and measured.
          }
        };

        rafId = requestAnimationFrame(measureIcons); // Start the measurement process on the next animation frame (animation frame refers to a single cycle of the browser’s rendering process, particularly when animations or visual updates are involved. Browsers aim to refresh the display at a smooth rate, typically 60 frames per second (fps), meaning the display updates every ~16.67 milliseconds.)

        return () => {
          cancelAnimationFrame(rafId); // The cleanup function returned by useEffect cancels any pending animation frames when the component unmounts or when the icons prop changes, preventing potential memory leaks or unnecessary function calls.
        };
      }
    }, [icons, userIconAssetsById]); // this useEffect hook calculates the bbox dimension and offset values of all icons

    const getUserIconAsset = (assetId) => {
      return userIconAssetsById[String(assetId)] || null;
    }; // get the resolved user uploaded icon asset data by its assetId

    return (
      <g>
        {/* defining a unique clipPath for this keycap using innerDPath1mmPadding: */}
        <defs>
          <clipPath id={clipPathId}>
            {/* Printable Area is currently disabled:
              <path d={innerDPath1mmPadding} />
            */}

            {/* The "new" printable area (grid), which is used instead of the original: */}
            {/* (values for Rect2 are only present for the ISO Enter key) */}
            <rect
              x={innerRect1X}
              y={innerRect1Y}
              width={innerRect1Width}
              height={innerRect1Height}
              fill="none"
            />
            {innerRect2X !== null &&
            innerRect2Y !== null &&
            innerRect2Width !== null &&
            innerRect2Height !== null ? (
              <rect
                x={innerRect2X}
                y={innerRect2Y}
                width={innerRect2Width}
                height={innerRect2Height}
                fill="none"
              />
            ) : null}
          </clipPath>
        </defs>

        <path
          ref={pathRef} // the path to calculate the bbox for the clipPath of the printable area
          d={innerDPath1mmPadding}
          fill="none" // not visible to the user
          id={innerPathId}
        />

        {/* Printable Area is currently disabled:
          {!dataComingFromLegendsEditor && !dataComingFromProcessOrderScreen && (
            // if the icon is being rendered in the Customizer:
            <path
              className="dont-include-in-download" // don't include this path in the svg and png downloads
              d={innerDPath1mmPadding}
              fill="none"
              stroke={getContrastingColor(keycapColor)}
              strokeWidth={
                selectedEditor === 'icons' &&
                showPrintableArea &&
                selectedKeycapRowIndex === rowIndex &&
                selectedKeycapIndex === keycapIndex &&
                selectedIconIndex
                  ? '0.1'
                  : '0'
              } // conditionally rendering the path to make the printable area visible
              id={printableAreaId}
            />
          )}
         */}

        {bbox &&
          icons &&
          Object.keys(icons).length > 0 && // Ensure icons is defined and not empty
          Object.entries(icons)
            .filter(([_, iconObj]) => isValidIconEntryForEditor(iconObj)) // filtering out empty strings (this ensures that only icons are being rendered onto the keycaps, if the string is not empty)
            .map(([index, iconObj]) => {
              const normalizedIcon = normalizeIconObject(iconObj);
              const { position } = normalizedIcon;
              const { gridStructure, isoSection } = position;

              if (
                selectedEditor === 'icons' &&
                showGrid &&
                selectedKeycapRowIndex === rowIndex &&
                selectedKeycapIndex === keycapIndex &&
                Number(selectedIconIndex) === Number(index)
              ) {
                return (
                  <g
                    className="dont-include-in-download" // don't include the grid in the svg and png downloads
                    key={index}
                  >
                    {/* Render grid outer rect */}
                    <g>
                      {isoSection === 'top' || isoSection === 'none' ? (
                        <rect
                          x={innerRect1X}
                          y={innerRect1Y}
                          width={innerRect1Width}
                          height={innerRect1Height}
                          fill="none"
                          stroke={getContrastingColor(keycapColor)}
                          strokeWidth="0.1"
                        />
                      ) : isoSection === 'main' &&
                        innerRect2X !== null &&
                        innerRect2Y !== null &&
                        innerRect2Width !== null &&
                        innerRect2Height !== null ? (
                        <rect
                          x={innerRect2X}
                          y={innerRect2Y}
                          width={innerRect2Width}
                          height={innerRect2Height}
                          fill="none"
                          stroke={getContrastingColor(keycapColor)}
                          strokeWidth="0.1"
                        />
                      ) : null}
                    </g>

                    {/* Render grid lines based on gridStructure */}
                    {(isoSection === 'top' || isoSection === 'none') && (
                      <g>
                        {gridStructure === '3x3' && (
                          <g>
                            {/* Render lines for 3x3 grid */}
                            {/* Vertical lines */}
                            <line
                              x1={innerRect1X + innerRect1Width / 3}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 3}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X + (innerRect1Width / 3) * 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + (innerRect1Width / 3) * 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal lines */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 3}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 3}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + (innerRect1Height / 3) * 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + (innerRect1Height / 3) * 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                        {gridStructure === '3x2' && (
                          <g>
                            {/* Render lines for 3x2 grid */}
                            {/* Vertical line */}
                            <line
                              x1={innerRect1X + innerRect1Width / 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal lines */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 3}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 3}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + (innerRect1Height / 3) * 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + (innerRect1Height / 3) * 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                        {gridStructure === '2x3' && (
                          <g>
                            {/* Render lines for 2x3 grid */}
                            {/* Vertical lines */}
                            <line
                              x1={innerRect1X + innerRect1Width / 3}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 3}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X + (innerRect1Width / 3) * 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + (innerRect1Width / 3) * 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal line */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                        {gridStructure === '2x2' && (
                          <g>
                            {/* Render lines for 2x2 grid */}
                            {/* Vertical line */}
                            <line
                              x1={innerRect1X + innerRect1Width / 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal line */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                      </g>
                    )}

                    {/* Render lines based on innerRect2 if isoSection is 'main' */}
                    {isoSection === 'main' &&
                      innerRect2X !== null &&
                      innerRect2Y !== null &&
                      innerRect2Width !== null &&
                      innerRect2Height !== null && (
                        <g>
                          {gridStructure === '3x3' && (
                            <g>
                              {/* Render lines for 3x3 grid */}
                              {/* Vertical lines */}
                              <line
                                x1={innerRect2X + innerRect2Width / 3}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 3}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X + (innerRect2Width / 3) * 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + (innerRect2Width / 3) * 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal lines */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 3}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 3}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + (innerRect2Height / 3) * 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + (innerRect2Height / 3) * 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                          {gridStructure === '3x2' && (
                            <g>
                              {/* Render lines for 3x2 grid */}
                              {/* Vertical line */}
                              <line
                                x1={innerRect2X + innerRect2Width / 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal lines */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 3}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 3}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + (innerRect2Height / 3) * 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + (innerRect2Height / 3) * 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                          {gridStructure === '2x3' && (
                            <g>
                              {/* Render lines for 2x3 grid */}
                              {/* Vertical lines */}
                              <line
                                x1={innerRect2X + innerRect2Width / 3}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 3}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X + (innerRect2Width / 3) * 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + (innerRect2Width / 3) * 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal line */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                          {gridStructure === '2x2' && (
                            <g>
                              {/* Render lines for 2x2 grid */}
                              {/* Vertical line */}
                              <line
                                x1={innerRect2X + innerRect2Width / 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal line */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                        </g>
                      )}
                  </g>
                );
              }
              return null;
            })}

        {/* applying the unique clipPath to ensure icons are clipped within the innerDPath1mmPadding */}
        <g clipPath={`url(#${clipPathId})`}>
          {bbox &&
            icons &&
            Object.keys(icons).length > 0 && // Ensure icons is defined and not empty
            Object.entries(icons)
              .filter(([_, iconObj]) => isRenderableIcon(iconObj)) // filtering out empty strings (this ensures that only icons are being rendered onto the keycaps, if the string is not empty)
              .map(([index, iconObj]) => {
                const normalizedIcon = normalizeIconObject(iconObj);
                const source = normalizedIcon.source;
                const {
                  icon,
                  style,
                  position,
                  isActiveStates,
                  assetId,
                  format,
                } = normalizedIcon;

                const userAsset =
                  source === 'user_asset' ? getUserIconAsset(assetId) : null; // get the user uploaded icon asset

                const userAssetMissing =
                  source === 'user_asset' &&
                  ((format === 'png' && !userAsset?.iconUrl) ||
                    (format === 'svg' && !userAsset?.svgInnerMarkup));

                const key = `${index}-${source}-${format || 'iconify'}-${
                  icon || assetId
                }`; // create a unique key

                const selectedBoundingBoxWidth =
                  position.boundingBox === 'compact'
                    ? dimensionsMap[index]?.width || style.width
                    : style.width; // Use bbox dimensions for "compact" or style dimensions for "standard"
                const selectedBoundingBoxHeight =
                  position.boundingBox === 'compact'
                    ? dimensionsMap[index]?.height || style.height
                    : style.height; // Use bbox dimensions for "compact" or style dimensions for "standard"

                const { x, y } = getLegendPosition(
                  position,
                  selectedBoundingBoxWidth,
                  selectedBoundingBoxHeight,
                  printableAreas
                ); // Calculate the x and y coordinates to position the icon within a grid cell

                return (
                  <g
                    key={key} // Include icon in key to force remounting on icon change (this makes sure, that the calculation of the bbox in the useEffect hook is made on the new icon. Otherwise, the useEffect would access the 'old' icon, as the new icon is not available for the first animation frame cycle of the useEffect hook.)
                    transform={`
                      translate(${x}, ${y})
                      ${
                        position.xOffset === undefined &&
                        position.yOffset === undefined
                          ? ''
                          : `translate(${
                              position.xOffset ? position.xOffset : 0
                            }, ${position.yOffset ? position.yOffset : 0})`
                      }
                      ${
                        position.rotation !== null &&
                        position.rotation !== undefined
                          ? `rotate(${position.rotation}, ${
                              selectedBoundingBoxWidth / 2
                            }, ${selectedBoundingBoxHeight / 2})`
                          : ''
                      }
                    `}
                  >
                    {selectedEditor === 'icons' &&
                    showBoundingBox &&
                    selectedKeycapRowIndex === rowIndex &&
                    selectedKeycapIndex === keycapIndex &&
                    Number(selectedIconIndex) === Number(index) ? (
                      <rect
                        className="dont-include-in-download" // don't include the visualization of the bbox in the svg and png downloads
                        width={selectedBoundingBoxWidth}
                        height={selectedBoundingBoxHeight}
                        fill="green"
                      />
                    ) : null}

                    <g
                      ref={(el) => (iconRefs.current[index] = el)} // Assign the ref to the <g> element (so that it can be accessed in the useEffect hook for bbox calculations)
                      transform={
                        position.boundingBox === 'compact'
                          ? `translate(${
                              -dimensionsMap[index]?.offsetX || 0
                            }, ${-dimensionsMap[index]?.offsetY || 0})`
                          : ''
                      } // if in 'compact' bbox mode, get rid of the icons offset (the distance between the svg canvas size at the position (0,0) and the position of the icons bbox)
                    >
                      {userAssetMissing ? (
                        <MissingUserIconAssetPlaceholder
                          width={style.width}
                          height={style.height}
                          color={getContrastingColor(keycapColor)}
                        />
                      ) : source === 'user_asset' && format === 'png' ? (
                        // user uploaded PNGs:
                        <g
                          transform={getFlipTransform(
                            style.flip,
                            style.width,
                            style.height
                          )}
                        >
                          <image
                            href={userAsset.iconUrl}
                            data-user-icon-asset-id={String(assetId)}
                            data-user-icon-format="png"
                            width={style.width}
                            height={style.height}
                            preserveAspectRatio="none"
                            onError={() =>
                              refreshUserIconAsset?.(String(assetId))
                            }
                          />
                        </g>
                      ) : source === 'user_asset' && format === 'svg' ? (
                        // user uploaded SVGs:
                        <g
                          transform={getFlipTransform(
                            style.flip,
                            style.width,
                            style.height
                          )}
                        >
                          <InlineSvgIcon
                            svgInnerMarkup={userAsset.svgInnerMarkup}
                            svgAttributes={userAsset.svgAttributes}
                            viewBox={userAsset.viewBox}
                            width={style.width}
                            height={style.height}
                            preserveAspectRatio="none"
                            fill={isActiveStates?.fill ? style.fill : undefined}
                            stroke={
                              isActiveStates?.stroke ? style.stroke : undefined
                            }
                            strokeWidth={
                              isActiveStates?.stroke
                                ? style.strokeWidth
                                : undefined
                            }
                          />
                        </g>
                      ) : (
                        // built-in Iconify icons:
                        <Icon
                          icon={icon}
                          width={style.width}
                          height={style.height}
                          preserveAspectRatio="none" // allow the svg to stretch
                          flip={style.flip}
                          color={isActiveStates?.fill ? style.fill : undefined}
                          style={{
                            strokeWidth: isActiveStates?.stroke
                              ? style.strokeWidth
                              : undefined,
                            stroke: isActiveStates?.stroke
                              ? style.stroke
                              : undefined,
                          }}
                        />
                      )}
                    </g>
                  </g>
                );
              })}
        </g>
      </g>
    );
  }
);

export default RenderKeycapIcons;
